import { trackAnalytics } from '@puffin/app-frame-api';
import type { router } from './routing/router';

type TrackingRouter = Pick<typeof router, 'subscribe'>;

let lastNavigationStartedInMs = window.performance.now();
let trackingInitialized = false;

const trackPageLoaded = () => {
  const navigationEntry = (window.performance.getEntriesByType('navigation') as PerformanceNavigationTiming[]).at(0);
  const durationInMs = Math.round(navigationEntry?.duration || window.performance.now());

  trackAnalytics('PAGE_LOADED', {
    durationInMs,
    ...(navigationEntry
      ? {
          domContentLoadedInMs: Math.round(navigationEntry.domContentLoadedEventEnd),
          responseLoadedInMs: Math.round(navigationEntry.responseEnd),
        }
      : {}),
  });
};

export const initializeUserTracking = (applicationRouter: TrackingRouter) => {
  if (trackingInitialized) {
    return () => undefined;
  }
  trackingInitialized = true;

  const unsubscribeBeforeLoad = applicationRouter.subscribe('onBeforeLoad', (event) => {
    if (!event.hrefChanged && event.fromLocation) {
      return;
    }

    lastNavigationStartedInMs = window.performance.now();
    trackAnalytics('NAVIGATION_CHANGE', {
      from: event.fromLocation?.pathname,
      to: event.toLocation.pathname,
      searchParameters: Object.keys(event.toLocation.search),
    });
  });

  const unsubscribeRendered = applicationRouter.subscribe('onRendered', (event) => {
    trackAnalytics('CONTENT_LOADED', {
      path: event.toLocation.pathname,
      durationSinceNavigationInMs: Math.max(0, Math.round(window.performance.now() - lastNavigationStartedInMs)),
    });
  });

  if (document.readyState === 'complete') {
    trackPageLoaded();
  } else {
    window.addEventListener('load', trackPageLoaded, { once: true });
  }

  return () => {
    trackingInitialized = false;
    unsubscribeBeforeLoad();
    unsubscribeRendered();
    window.removeEventListener('load', trackPageLoaded);
  };
};
